import React from "react";
import {
  ResponsiveHeroImage,
  LoadMoreButton,
  LoadMoreStoriesBase,
  ReviewRating,
  ResponsiveImage
} from "@quintype/components";
import { CustomLink } from "../custom-link";
import TimeAgo from "react-timeago";
import { AdService } from "../ad-service";
import { ImageFallback } from "../basic/image-fallback";
import format from "date-fns/format";
import get from "lodash/get";
import { isEmpty, isExternalStory, updateStoriesWithAlternatives } from "../../utils";
import PropTypes from "prop-types";
import { SvgHandler } from "../svg-handler/svg-handler";
import twitterIcon from "../../../assets/icons/twitter.svg";

function formatter(value, unit, suffix, date, defaultFormatter) {
  return format(new Date(date), "DD MMM, YYYY [at] HH:mm A");
}

const getAuthorImage = ({ name = "", "avatar-url": avatarUrl, "avatar-s3-key": avatarS3Key }) => {
  if (!avatarS3Key && !avatarUrl) return null;

  return (
    <div className="author-detail__avatar">
      <figure>
        {avatarS3Key ? (
          <ResponsiveImage
            slug={avatarS3Key}
            aspect-ratio={[1, 1]}
            defaultWidth={400}
            imgParams={{ auto: ["format", "compress"] }}
            alt={name}
          />
        ) : (
          <img src={avatarUrl} alt={name} />
        )}
      </figure>
    </div>
  );
};
getAuthorImage.propTypes = {
  name: PropTypes.string,
  "avatar-url": PropTypes.string,
  "avatar-s3-key": PropTypes.string
};

const AuthorDetails = ({ author }) => {
  return (
    <section className="author-detail">
      {getAuthorImage(author)}
      <div className="author-detail__content">
        <h1 className="author-detail__name">{author.name}</h1>
        {author.bio && <div className="author-detail__bio" dangerouslySetInnerHTML={{ __html: author.bio }} />}
        <div className="author-detail__social">
          {author["twitter-handle"] && (
            <a href={"http://twitter.com/" + author["twitter-handle"]} target="_blank" rel="noopener noreferrer">
              <SvgHandler width="24px" height="24px" clazzName="twitter" xlinkHref={twitterIcon} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
AuthorDetails.propTypes = {
  author: PropTypes.object
};

const AuthorMainStories = props => {
  const stories = props.stories || [];
  return (
    <section className="author-main-stories">
      {stories.slice(0, 3).map((storyData = {}) => {
        const { story = storyData } = storyData; // because of inconsistent api response structure
        const isReviewStory = story["story-template"] === "review";
        return (
          <div className="author-main-stories__story" key={story.id}>
            <CustomLink
              href={"" + story.url}
              externalLink={isExternalStory(story)}
              className="author-main-stories__story__link"
            >
              <div className="author-main-stories__story__avatar">
                {story["hero-image-s3-key"] ? (
                  <figure
                    className={`author-main-stories__story-image author-main-stories__story-image--${
                      story["story-template"]
                    }`}
                  >
                    <ResponsiveHeroImage
                      story={story}
                      aspectRatio={[9, 3]}
                      defaultWidth={480}
                      widths={[300, 400, 500]}
                      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
                      imgParams={{ auto: ["format", "compress"], fit: "max" }}
                    />
                  </figure>
                ) : (
                  <ImageFallback story={story} />
                )}
              </div>
              <div className="author-main-stories__story__content">
                <div className="author-main-stories__story-headline-rating-wrapper">
                  <h3
                    className="author-main-stories__story-headline"
                    dangerouslySetInnerHTML={{ __html: story.headline }}
                  />
                  {isReviewStory && <ReviewRating value={get(story, ["metadata", "review-rating", "value"], "0")} />}
                </div>
                {story["published-at"] && (
                  <p className="author-main-stories__story-published-at">
                    Posted
                    <TimeAgo date={story["published-at"]} title={null} formatter={formatter} />
                  </p>
                )}
              </div>
            </CustomLink>
          </div>
        );
      })}
    </section>
  );
};
AuthorMainStories.propTypes = {
  stories: PropTypes.array
};

const AuthorStories = ({ stories, onLoadMore, noMoreStories }) => {
  const updatedStoriesWithAlternatives = updateStoriesWithAlternatives(stories);
  return (
    <section className="author-loadmore-stories">
      {updatedStoriesWithAlternatives.slice(3).map(item => {
        let story = item.story || item;
        return (
          <div className="author-loadmore-stories__story" key={story.id}>
            <CustomLink
              href={"" + story.url}
              externalLink={isExternalStory(story)}
              className="author-loadmore-stories__story__link"
            >
              <div className="author-loadmore-stories__story__avatar">
                {story["hero-image-s3-key"] ? (
                  <figure
                    className={`author-loadmore-stories__story-image author-loadmore-stories__story-image--${
                      story["story-template"]
                    }`}
                  >
                    <ResponsiveHeroImage
                      story={story}
                      aspectRatio={[4, 3]}
                      defaultWidth={480}
                      widths={[300, 400, 500]}
                      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
                      imgParams={{ auto: ["format", "compress"], fit: "max" }}
                    />
                  </figure>
                ) : (
                  <ImageFallback story={story} />
                )}
              </div>
              <div className="author-loadmore-stories__story__content">
                <h3
                  className="author-loadmore-stories__story-heading"
                  dangerouslySetInnerHTML={{ __html: story.headline }}
                />
                {story["published-at"] && (
                  <p className="author-main-stories__story-published-at">
                    Posted <TimeAgo date={story["published-at"]} title={null} formatter={formatter} />
                  </p>
                )}
              </div>
            </CustomLink>
          </div>
        );
      })}
      {!noMoreStories && (
        <div className="load-more-stories ">
          <LoadMoreButton className="load-more-button" onLoadMore={onLoadMore} text="Load More" />
        </div>
      )}
    </section>
  );
};
AuthorStories.propTypes = {
  stories: PropTypes.array,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool
};
const apiResponseTransformer = response => response.items;
const AuthorPage = props => {
  const updatedStoriesWithAlternatives = updateStoriesWithAlternatives(props.data.stories);
  const fields =
    "alternative,metadata,story-template,id,hero-image-s3-key,hero-image-metadata,headline,published-at,slug,url";
  const authorId = get(props, ["data", "author", "id"]);
  const numStoriesToLoad = 20;
  return !isEmpty(props.data.author) ? (
    <div className="author-detail-page">
      <AuthorDetails author={props.data.author} />
      <section className="author-page-dfp">
        <AdService adtype="Mrec" slotId="Author-Mrec" />
      </section>
      {get(props.data, ["stories"], []).length ? (
        <React.Fragment>
          <AuthorMainStories stories={updatedStoriesWithAlternatives.slice(0, 3)} />
          <LoadMoreStoriesBase
            api={`/api/v1/authors/${authorId}/collection`}
            template={AuthorStories}
            apiResponseTransformer={apiResponseTransformer}
            fields={fields}
            {...props}
            params={{ "author-id": authorId, sort: "published-at" }}
            numStoriesToLoad={numStoriesToLoad}
          />
        </React.Fragment>
      ) : (
        <h1>No stories found for this author</h1>
      )}
    </div>
  ) : (
    <div className="author-detail-page">
      <h1>Author not found</h1>
    </div>
  );
};

AuthorPage.propTypes = {
  data: PropTypes.object
};

export { AuthorPage };
